import { useCallback, useEffect, useRef, useState } from 'react';
import { cloneStylesheets } from './cloneStylesheets';
import type { DocumentPictureInPictureWindow } from '../types';

const PIP_WIDTH = 420;
const PIP_HEIGHT = 400;

export function usePipWindow() {
  const [pipWindow, setPipWindow] =
    useState<DocumentPictureInPictureWindow | null>(null);
  const pipWindowRef = useRef<DocumentPictureInPictureWindow | null>(null);

  const isSupported = 'documentPictureInPicture' in window;

  const open = useCallback(async () => {
    if (!isSupported) return;

    // Don't open if one already exists
    if (window.documentPictureInPicture?.window) return;

    try {
      const win = await window.documentPictureInPicture!.requestWindow({
        width: PIP_WIDTH,
        height: PIP_HEIGHT,
      });

      cloneStylesheets(win.document);

      pipWindowRef.current = win;
      setPipWindow(win);

      const handlePageHide = () => {
        pipWindowRef.current = null;
        setPipWindow(null);
      };
      win.addEventListener('pagehide', handlePageHide, { once: true });
    } catch (error) {
      // Expected when no recent user gesture — not an error worth surfacing
      console.debug('[PiP] requestWindow failed:', error);
    }
  }, [isSupported]);

  const close = useCallback(() => {
    pipWindowRef.current?.close();
    pipWindowRef.current = null;
    setPipWindow(null);
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      pipWindowRef.current?.close();
    };
  }, []);

  return { pipWindow, isOpen: pipWindow !== null, isSupported, open, close };
}
